import React, { useState, useEffect } from 'react';
import { Lock, Crown, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePremium } from '../../../hooks/usePremium';
import { shouldShowPremiumUI } from '../../../config/premiumConfig';
import { isNativeApp } from '../../../utils/platform';
import { Modal } from '../Modal';
import { MembershipBenefitsText } from '../../MembershipBenefitsText';
import { MemberSurvey } from '../../survey/MemberSurvey';
import { ACTIVE_SURVEY } from '../../../config/surveyConfig';
import { get } from '../../../utils/api';

interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  requiresAuth?: boolean;
  requiresPremium?: boolean;
  feature?: string;
}

/**
 * Generic upgrade prompt modal
 * Shows appropriate CTA based on user state (anonymous vs free user)
 * Premium path now shows a simple teaser that links to /membership
 */
export const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  requiresAuth = false,
  requiresPremium = false,
  feature
}) => {
  const navigate = useNavigate();
  const { isPremium, isCancelling, subscriptionEndDate, planType } = usePremium();
  const [surveyCompleted, setSurveyCompleted] = useState<boolean | null>(null);

  const isPremiumPrompt = requiresPremium && !requiresAuth;
  const shouldRedirectNativePremiumPrompt = isOpen && isPremiumPrompt && !isPremium && isNativeApp();

  // RevenueCatUI's SwiftUI paywall crashes on iOS 17. Route native users to
  // the membership page, which uses RevenueCat's non-UI purchasePackage flow.
  useEffect(() => {
    if (!shouldRedirectNativePremiumPrompt) return;
    onClose();
    navigate('/membership');
  }, [shouldRedirectNativePremiumPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || !isPremium || !isPremiumPrompt || !ACTIVE_SURVEY) return;
    let cancelled = false;
    get<{ completed: boolean }>(`/api/survey/member?surveyId=${ACTIVE_SURVEY.surveyId}`)
      .then(res => { if (!cancelled) setSurveyCompleted(res.data.completed); })
      .catch(() => { if (!cancelled) setSurveyCompleted(true); }); // hide survey on error
    return () => { cancelled = true; };
  }, [isOpen, isPremium, isPremiumPrompt]);

  // Don't show modal if upgrade modals are globally disabled
  if (!shouldShowPremiumUI('showModals')) {
    return null;
  }

  // On native, redirect to /membership and don't render the React modal.
  if (shouldRedirectNativePremiumPrompt) {
    return null;
  }

  const handlePrimaryAction = () => {
    if (requiresAuth) {
      onClose();
      window.dispatchEvent(new CustomEvent('open-user-modal-for-checkout'));
    } else if (requiresPremium) {
      onClose();
      navigate('/membership');
    }
  };

  // Already premium
  if (isPremium && isPremiumPrompt) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        titleCentered
        showCloseButton
        animationStyle="slide-up"
        className="max-w-[90vw] sm:max-w-sm"
      >
        <div className="space-y-3">
          <div className="flex justify-center">
            <Crown className="w-10 h-10 text-amber-500" />
          </div>
          <div className="text-center space-y-0.5">
            <h2 className="text-xl font-bold text-foreground">Thanks for being a Wallstreetle Member!</h2>
            {planType === 'lifetime_og' ? (
              <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                Lifetime membership
              </p>
            ) : planType === 'special_3m' ? (
              <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                Special Premium (3m)
              </p>
            ) : isCancelling && subscriptionEndDate ? (
              <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                Subscription ends on {new Date(subscriptionEndDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
              </p>
            ) : null}
          </div>

          {ACTIVE_SURVEY && surveyCompleted !== null && (
            <>
              <p style={{ margin: "0 0 14px", fontSize: "11px", fontWeight: "700", letterSpacing: "1.5px", textTransform: "uppercase", color: "#999", fontFamily: "sans-serif" }}>Member Survey</p>
              {surveyCompleted ? (
                <div className="flex flex-col items-center gap-1.5 rounded-lg border border-green-300 bg-green-50 px-3 py-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <p className="text-sm font-semibold text-green-800 text-center">
                    Thank You For Answering The {new Date().toLocaleString('en-US', { month: 'long' })} Member Survey
                  </p>
                  <p className="text-xs text-gray-500 text-center">
                    We welcome any other thoughts or feedback through our{' '}
                    <a href="https://wallstreetle.com/feedback" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">feedback form</a>
                  </p>
                </div>
              ) : (
                <MemberSurvey
                  surveyId={ACTIVE_SURVEY.surveyId}
                  question={ACTIVE_SURVEY.question}
                  options={ACTIVE_SURVEY.options}
                  onSubmitted={() => setSurveyCompleted(true)}
                />
              )}
              <hr className="border-border" />
            </>
          )}
          <p style={{ margin: "0 0 14px", fontSize: "11px", fontWeight: "700", letterSpacing: "1.5px", textTransform: "uppercase", color: "#999", fontFamily: "sans-serif" }}>Member Benefits</p>
          <MembershipBenefitsText />
          <p className="text-sm text-center leading-relaxed rounded-lg px-3 py-2" style={{ background: '#f7f3ee', color: '#1c1c1c', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
            Your support keeps Wallstreetle free and helps us build new features<br /> that make learning the stock market easier.
          </p>
          <p className="text-xs text-center text-muted-foreground leading-relaxed">
            You can manage your subscription<br />in account settings found in your profile.
          </p>
        </div>
      </Modal>
    );
  }

  // Requires auth (sign-up prompt)
  if (requiresAuth) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        titleCentered
        showCloseButton
        animationStyle="slide-up"
        className="max-w-lg sm:max-w-xl"
      >
        <div className="space-y-4 sm:space-y-6">
          <div className="flex justify-center">
            <Lock className="w-12 h-12 text-blue-500" />
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">
              {title || 'Sign Up to Continue'}
            </h2>
            <p className="text-muted-foreground">
              {message || 'Create a free account to unlock this feature'}
            </p>
            {feature && (
              <p className="text-sm text-muted-foreground italic">Feature: {feature}</p>
            )}
          </div>
          <div className="space-y-3">
            <button
              onClick={handlePrimaryAction}
              className="w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors duration-200 ease-theme bg-blue-600 hover:bg-blue-700"
            >
              Sign Up Free
            </button>
            <button
              onClick={onClose}
              className="w-full py-2 px-4 rounded-lg font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200 ease-theme"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Premium teaser modal
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleCentered
      showCloseButton
      animationStyle="slide-up"
      className="!w-fit max-w-[90vw] sm:max-w-sm"
    >
      <div className="space-y-4 sm:space-y-6">
        <div className="flex justify-center">
          <Crown className="w-12 h-12 text-amber-500" />
        </div>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground">{title || 'Member-Only Feature'}</h2>
        </div>
        <MembershipBenefitsText />
        <div className="space-y-1">
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-xs text-muted-foreground">No commitment, cancel anytime</span>
          </div>
          <button
            onClick={handlePrimaryAction}
            className="w-full py-3 px-4 rounded-full font-semibold text-white transition-all duration-200 ease-theme bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 bg-[length:200%_100%] hover:bg-[position:100%_0] hover:shadow-md"
          >
            Continue
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 px-4 rounded-full font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200 ease-theme"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UpgradePromptModal;
